import { useCallback } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { Msg } from "../../../types/chat";
import { safeUuid } from "../chatUtils";
import type { NoteDraft } from "../chatTypes";
import type { OutboxItem } from "./useOutbox";

const fq = (vId: string, cId: string) => (cId.includes(':') ? cId : `${vId}:${cId}`);

/**
 * Create, edit and delete the notes in a Notes channel. A note is a message: creating one adds it to the list
 * straight away and sends it (or queues it while offline); editing and deleting tell the server and update the list.
 */
export function useNotesActions({ socket, currentVoidId, currentChannelId, setMsgsByKey, emitOrQueue }: {
  socket: { emit: (event: string, payload?: unknown) => unknown };
  currentVoidId: string;
  currentChannelId: string;
  setMsgsByKey: Dispatch<SetStateAction<Record<string, Msg[]>>>;
  emitOrQueue: (payload: OutboxItem) => void;
}) {
  const formatNoteContent = (title: string, body: string, tags: string[]) => {
    const sections: string[] = [];
    if (title.trim()) sections.push(title.trim());
    if (body.trim()) sections.push(body.trim());
    if (tags.length) sections.push(tags.map((tag) => (tag.startsWith('#') ? tag : `#${tag}`)).join(' '));
    return sections.join('\n\n');
  };

  const createNote = useCallback(async ({ title, body, tags }: NoteDraft) => {
    if (!currentVoidId || !currentChannelId) return;
    const content = formatNoteContent(title, body, tags);
    if (!content) return;
    const tempId = safeUuid();
    const fid = fq(currentVoidId, currentChannelId);
    setMsgsByKey((old) => {
      const list = old[fid] || [];
      return { ...old, [fid]: [...list, { id: tempId, content, optimistic: true, createdAt: new Date().toISOString() }] };
    });
    emitOrQueue({ voidId: currentVoidId, channelId: fid, content, tempId, attachments: [] });
  }, [currentVoidId, currentChannelId, emitOrQueue, setMsgsByKey]);

  const updateNote = useCallback(async (noteId: string, draft: NoteDraft) => {
    if (!currentVoidId || !currentChannelId) return;
    const content = formatNoteContent(draft.title, draft.body, draft.tags);
    if (!content) return;
    socket.emit('message:edit', { messageId: noteId, content });
    const fid = fq(currentVoidId, currentChannelId);
    setMsgsByKey((old) => {
      const list = old[fid] || [];
      const idx = list.findIndex((m) => m.id === noteId);
      if (idx === -1) return old;
      const copy = [...list];
      copy[idx] = { ...copy[idx], content, optimistic: true };
      return { ...old, [fid]: copy };
    });
  }, [currentVoidId, currentChannelId, socket, setMsgsByKey]);

  const deleteNote = useCallback(async (noteId: string) => {
    if (!currentVoidId || !currentChannelId) return;
    socket.emit('message:delete', { messageId: noteId });
    const fid = fq(currentVoidId, currentChannelId);
    setMsgsByKey((old) => {
      const list = old[fid] || [];
      if (!list.some((m) => m.id === noteId)) return old;
      return { ...old, [fid]: list.filter((m) => m.id !== noteId) };
    });
  }, [currentVoidId, currentChannelId, socket, setMsgsByKey]);
  return { createNote, updateNote, deleteNote };
}
